import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { empty } from './utils/empty'
import { parseSettings } from './utils/parseSettings'
import { camelCase } from '@ampcore/string-format'
import { ComponentRegistry } from './types/component-registry.type.js'

export type AttributeValue =
  | string
  | number
  | boolean
  | AttributeValue[]
  | { [key: string]: AttributeValue }

export const COMPONENT_ATTR = 'data-is'
export const SETTINGS_ATTR = 'data-settings'
export const INITIALIZED_COMPONENT_CLASS = 'component-loaded'

export function loadComponents(componentRegistry: ComponentRegistry) {
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(
      `[${COMPONENT_ATTR}]:not(.${INITIALIZED_COMPONENT_CLASS})`
    )
  )

  const initPromises = []

  for (const component of components) {
    initPromises.push(
      (async () => {
        try {
          await initComponent(component, componentRegistry)
        } catch {
          const key = component?.getAttribute?.(COMPONENT_ATTR) || '[unknown]'
          console.error(`Error initializing ${key}`)
        }
      })()
    )
  }

  Promise.all(initPromises)
    .then(() => components.length)
    .catch(() => {
      console.error(`Error initializing ${components.length} components`)
      return components.length
    })

  return components.length
}

export function initComponent(
  el: HTMLElement,
  componentRegistry: ComponentRegistry
): Root | undefined {
  el.classList.add(INITIALIZED_COMPONENT_CLASS)
  const { name, settings } = parseElement(el)
  const content = parseElementAttributes(el)

  if (!(name in componentRegistry)) {
    return
  }

  const ReactComponent = componentRegistry[name]?.component

  if (!ReactComponent) {
    return
  }

  const root = createRoot(empty(el))
  root.render(React.createElement(ReactComponent, { ...settings, ...content }))
  return root
}

export function parseElement(el: HTMLElement): {
  name: string
  settings: Record<string, unknown>
} {
  const name = el.getAttribute(COMPONENT_ATTR) || ''
  const settings = el.getAttribute(SETTINGS_ATTR) || ''

  return {
    name,
    settings: parseSettings(settings)
  }
}

export function parseElementAttributes(
  el: HTMLElement
): Record<string, AttributeValue> {
  const attributes: Record<string, AttributeValue> = {}

  // Direct attributes, like <attribute>=<value>
  Array.from(el.attributes).forEach(attribute => {
    attributes[parseAttributeName(attribute.name)] = attribute.value
  })
  return Object.assign(
    attributes,
    parseChildAttributes(Array.from(el.children) as HTMLElement[])
  )
}

export function parseChildAttributes(
  children: HTMLElement[]
): Record<string, AttributeValue> {
  const attributes: Record<string, AttributeValue> = {}

  children
    .filter(
      child =>
        child.hasAttribute('data-attribute') || child.tagName.includes('-')
    )
    .forEach(child => {
      const name =
        child.getAttribute('data-attribute') ||
        parseAttributeName(child.tagName)
      const hasAttributes = child.attributes.length > 0
      let val
      if (!hasChildAttributes(child)) {
        val = hasAttributes ? parseElementAttributes(child) : child.innerHTML
      } else {
        val = parseElementAttributes(child)
      }

      if (name in attributes && !Array.isArray(attributes[name])) {
        attributes[name] = [attributes[name]]
      }

      if (name in attributes) {
        (attributes[name] as AttributeValue[]).push(val)
      } else {
        attributes[name] = val
      }
    })

  return attributes
}

export function parseAttributeName(name: string) {
  if (name.indexOf('-') !== -1) {
    return camelCase(name.toLowerCase().replace(/^data-/, ''))
  }
  return name
}

export function hasChildAttributes(el: HTMLElement): boolean {
  return (
    el.hasChildNodes() &&
    el.children &&
    el.children[0] &&
    el.children[0].tagName.indexOf('-') !== -1
  )
}

export default loadComponents
